/* ═══════════════════════════════════════════════════════════════════════════
   Chase N Chew — header behaviour

   Deliberately a separate file from cnc-app.js. That file owns the cart
   drawer and is loaded by every section; the header is loaded once, on every
   page, and the two have no shared state beyond `window.CNC`. Keeping them
   apart means a header bug can't take the cart down with it.

   What this does:
     · cross-fades the search placeholder through SEARCH_HINTS
     · hands search off to the HOST THEME's search drawer (never a second one)
     · opens the host minicart for the bag icon
     · keeps the cart / wishlist badges hidden while they read zero
     · a delivery-location popover: pincode for guests, the customer's saved
       address book (via the /apps/cnc App Proxy) once they're signed in
     · the rewards icon is a plain link — no balance is fetched (see below)

   Vanilla, no dependencies, safe to load twice.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  if (window.__cncHeaderLoaded) return;
  window.__cncHeaderLoaded = true;

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var PIN_KEY = "cnc:pincode";
  var NEW_KEY = "cnc:rewards-new";

  window.CNC = window.CNC || {};

  /* ── Search ─────────────────────────────────────────────────────────────
     The store already has a search drawer — the host theme's. Rendering a
     second one would mean two predictive-search implementations, two sets of
     results and two places for a query to get lost, so this only ever *opens*
     the existing one.

     Finding it is the awkward part, because the trigger's markup is the
     theme's business and survives no upgrade promise. Three layers, most
     specific first:

       1. `search_trigger` on the section — a CSS selector the merchant can
          paste in when the theme changes and layers 2/3 stop matching. This
          is the escape hatch; it needs no code change.
       2. a list of selectors used by the themes this store has run.
       3. no trigger found → the field is a plain GET form to /search and
          submits like any other. Search still works with the drawer missing,
          which is the whole point of building it as a form.
  ──────────────────────────────────────────────────────────────────────── */

  var SEARCH_TRIGGERS = [
    "[data-cnc-search-open]",
    '[data-drawer-open="search"]',
    "[data-open-search]",
    "search-drawer-button",
    ".search-drawer-button",
    ".header__search-toggle",
    ".header__icon--search",
    ".site-header__search-toggle",
    ".js-search-open",
    "button.search-icon",
    "a.search-icon",
    ".header-search-toggle",
    '[href="/search"].header__icon'
  ];

  /* Classes themes hang off <html> to reveal a search drawer. Same shape as
     cnc-app.js's DRAWER_HTML_CLASSES for the minicart. */
  var SEARCH_HTML_CLASSES = ["open-search", "open-search-drawer", "search-opened"];
  var SEARCH_PANEL_SEL = "#search-drawer, .search-drawer, #Search-Drawer, .header__search-drawer, .drawer--search";

  function visible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  function findSearchTrigger(root) {
    var custom = root && root.dataset.searchTrigger;
    var list = custom ? [custom].concat(SEARCH_TRIGGERS) : SEARCH_TRIGGERS;
    for (var i = 0; i < list.length; i++) {
      var nodes;
      /* A merchant-supplied selector is untrusted input — an invalid one
         throws inside querySelectorAll and would otherwise kill search
         entirely rather than falling through to the next candidate. */
      try { nodes = document.querySelectorAll(list[i]); } catch (e) { continue; }
      for (var j = 0; j < nodes.length; j++) {
        /* Skip our own field, and skip triggers the theme has hidden at this
           breakpoint — clicking a display:none mobile button does nothing and
           looks, from the customer's side, like search is broken. */
        if (root && root.contains(nodes[j])) continue;
        if (visible(nodes[j])) return nodes[j];
      }
    }
    return null;
  }

  function drawerInput() {
    var panel = document.querySelector(SEARCH_PANEL_SEL);
    var scope = panel || document;
    var candidates = scope.querySelectorAll(
      'input[type="search"], input[name="q"], predictive-search input'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].classList.contains("cnc-hd-input")) continue;
      if (visible(candidates[i])) return candidates[i];
    }
    return null;
  }

  /* Returns true if a real drawer took over. `seed` is whatever the customer
     had already typed into our field — it moves across so the keystrokes
     aren't thrown away. */
  function openSearch(root, seed) {
    var trigger = findSearchTrigger(root);
    if (trigger) {
      trigger.click();
    } else if (document.querySelector(SEARCH_PANEL_SEL)) {
      SEARCH_HTML_CLASSES.forEach(function (c) { document.documentElement.classList.add(c); });
    } else {
      return false;
    }

    /* The panel is animated in, so its input isn't focusable on this frame.
       Poll briefly rather than guessing a duration — themes disagree on
       transition length and a fixed setTimeout is a race either way. */
    var tries = 0;
    (function grab() {
      var input = drawerInput();
      if (input) {
        if (seed) {
          input.value = seed;
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
        try { input.focus(); } catch (e) { /* focus can throw on a hidden node */ }
        return;
      }
      if (++tries < 20) requestAnimationFrame(grab);
    })();
    return true;
  }
  window.CNC.openSearch = function (seed) { return openSearch(null, seed || ""); };

  function initSearch(root) {
    var field = root.querySelector("[data-cnc-hd-field]");
    var input = root.querySelector(".cnc-hd-input");
    if (!field || !input) return;

    /* `search_mode` decides who owns the keystroke, and there must only ever be
       one owner — that is the same rule the drawer handoff above was written
       for, applied now that there is a second candidate.

         predictive  cnc-search.js mounts its panel on this input. We bind
                     nothing; a handoff would open the theme's drawer over our
                     own panel and split the query between two inputs.
         drawer      the original behaviour, below.
         form        neither. The field is a GET form and submits on Enter.

       Default is `predictive`. Measured on the replica on 2026-08-22: no
       element matching any selector in SEARCH_TRIGGERS was present or visible,
       and no panel matched SEARCH_PANEL_SEL, so `openSearch` returned false on
       every keystroke and typing in the header did nothing at all. Handing off
       to a drawer this store does not have was the status quo, not a feature.
       `drawer` keeps it available for a theme that does. */
    var mode = (root.dataset.searchMode || "predictive");
    if (mode === "predictive" || mode === "form") return;

    /* WHEN the drawer opens: on the first character typed, not on click or
       focus.

       Opening on focus was the first cut and it's the wrong trade. A customer
       who clicks the field — or tabs through the header, or taps it by
       accident on a phone — gets a full-screen panel thrown at them before
       they've asked for anything, and on mobile that also means the keyboard
       and the drawer animating at once. Typing is the unambiguous signal that
       they actually want to search, and it matches what the theme's own search
       icon does once you're inside its drawer.

       The keystroke isn't lost: `seed` carries whatever is in the field into
       the drawer's input, which is then focused, so the customer keeps typing
       without noticing the handover. */
    var handing = false;

    function handoff(e) {
      if (handing) return;
      handing = true;
      var seed = input.value;
      if (openSearch(root, seed)) {
        if (e) e.preventDefault();
        input.blur();
        input.value = "";
      }
      /* Latch. Blurring our field to focus the drawer's fires our own focus
         handler again on the way back, and without this the drawer reopens in
         a loop. */
      setTimeout(function () { handing = false; }, 400);
    }

    input.addEventListener("input", function () {
      if (input.value) handoff();
    });

    /* Paste, autofill and speech input don't always emit `input` in older
       WebKit — `change` catches what slips through. */
    input.addEventListener("change", function () {
      if (input.value) handoff();
    });

    /* Belt and braces: if no drawer was found, the form still submits. Adding
       type=product keeps pages and articles out of the results, which is what
       the app's searchProducts() returns. */
    var form = root.querySelector("[data-cnc-hd-form]");
    if (form) {
      form.addEventListener("submit", function (e) {
        if (!input.value.trim()) { e.preventDefault(); handoff(); }
      });
    }
  }

  /* ── Rotating hints ─────────────────────────────────────────────────────
     Mirrors AnimatedSearchBar: 2s per hint, 300ms cross-fade. Frozen while
     the field is focused or holds a query — a placeholder that keeps moving
     under a half-typed word is just noise. */
  function initHints(root) {
    var input = root.querySelector(".cnc-hd-input");
    if (!input || reduceMotion) return;
    var hints;
    try { hints = JSON.parse(root.dataset.hints || "[]"); } catch (e) { return; }
    if (!hints.length) return;

    var i = 0, focused = false;
    input.addEventListener("focus", function () { focused = true; });
    input.addEventListener("blur", function () { focused = false; });

    setInterval(function () {
      if (focused || input.value || document.hidden) return;
      input.classList.add("is-fading");
      setTimeout(function () {
        i = (i + 1) % hints.length;
        input.placeholder = "Search for " + hints[i];
        input.classList.remove("is-fading");
      }, 300);
    }, 2000);
  }

  /* ── Bag ────────────────────────────────────────────────────────────────
     cnc-app.js owns the minicart and exports openCart. If it hasn't loaded
     (a page with no CNC sections on it), the anchor's href="/cart" carries
     the click and the customer lands on the cart page. */
  function initCart(root) {
    var btn = root.querySelector("[data-cnc-hd-cart]");
    if (!btn) return;

    btn.addEventListener("click", function (e) {
      var api = window.CNC;
      if (!api || typeof api.openCart !== "function") return;   // href → /cart
      // Ask before committing: preventDefault on a click we then can't act on
      // leaves the customer with a bag button that does nothing at all.
      if (typeof api.hasCart === "function" && !api.hasCart()) return;

      e.preventDefault();

      /* Opened on the NEXT TICK, not in this handler.

         Opening synchronously marks the drawer open, and then this very click
         carries on bubbling to document — where every outside-click handler
         on the page, ours in cnc-app.js and whatever the host theme has of
         its own, sees an open drawer and a target outside the panel and
         closes it again. The drawer opened and shut inside one event, which
         from the outside looked exactly like a dead button.

         Deferring by one task lets the click finish first, so those handlers
         run while the drawer is still closed and correctly do nothing.
         cnc-app.js also excludes this button explicitly; belt and braces,
         because the theme's handlers are not ours to fix. */
      setTimeout(function () { api.openCart(); }, 0);
    });
  }

  /* ── Badges ─────────────────────────────────────────────────────────────
     Both counters are written by code we don't own on this element:
     cnc-app.js copies the theme's `.cart-count` innerHTML across after every
     cart change, and sets `.wishlist-count` textContent on every heart. Both
     write the number and nothing else, so an `is-empty` class set at render
     time goes stale the moment either fires.

     Watching the node and deriving the class from its text is the only thing
     that stays correct regardless of who wrote the number. */
  function initBadges(root) {
    var badges = root.querySelectorAll("[data-cnc-hd-badge]");
    if (!badges.length) return;

    function paint(el) {
      var n = parseInt((el.textContent || "").replace(/[^\d]/g, ""), 10);
      el.classList.toggle("is-empty", !n);
    }

    Array.prototype.forEach.call(badges, function (el) {
      paint(el);
      new MutationObserver(function () { paint(el); })
        .observe(el, { childList: true, characterData: true, subtree: true });
    });

    /* The wishlist count is client-side only, so the server-rendered value is
       always 0 on first paint. Seed it from the same key cnc-app.js uses. */
    var wish = root.querySelector("[data-cnc-hd-wishcount]");
    if (wish) {
      var list = [];
      try { list = JSON.parse(localStorage.getItem("glozin__wishlist-items")) || []; } catch (e) { list = []; }
      if (Array.isArray(list)) wish.textContent = list.length;
    }
  }

  /* ── The rewards "NEW" flag ───────────────────────────────────────────────
     A badge on a nav icon is a promise that something behind it changed. What
     makes it worth anything is that it stops — a flag that is still there on
     the twentieth visit has taught the visitor that flags in this header mean
     nothing, and it takes the cart badge's credibility down with it.

     So it retires three ways, and the markup ships with the flag OFF until
     one of them has been ruled out:

       clicked    they acted on it. Recorded on the click rather than on the
                  rewards page's load, because middle-click, a slow network
                  and a change of mind all lose the dismissal otherwise.
       seen it    they are standing on the rewards page already, however they
                  arrived. It is not news to them.
       aged out   N days from THEIR first sight of it, not from launch — a
                  visitor who first shows up in week four still gets the full
                  run, and the merchant who set it never has to remember to
                  come back and switch it off.

     Re-announcing is a wording change in the theme editor: the dismissal is
     keyed by the badge's own word, so a different word is a different
     announcement and reaches everyone again. Turning the setting off and on
     deliberately does NOT, which is the behaviour a merchant wants while they
     are fiddling with the header.

     One key, one small object, and every read and write wrapped: private mode
     throws on the write, and the worst that does is show the flag again next
     visit. Nothing here is allowed to be load-bearing. */
  function initNewBadge(root) {
    var link = root.querySelector("[data-cnc-newbadge]");
    if (!link) return;

    var marks = link.querySelectorAll("[data-cnc-new-mark]");
    if (!marks.length) return;

    var label = (link.getAttribute("data-new-label") || "NEW").replace(/^\s+|\s+$/g, "");
    var days = parseInt(link.getAttribute("data-new-days"), 10);
    if (!(days > 0)) days = 0;

    /* The retired wording, kept so the flag can be taken back out of it. */
    var baseAria = link.getAttribute("aria-label") || "";

    function arm() {
      Array.prototype.forEach.call(marks, function (m) { m.classList.add("is-armed"); });
      /* The marks are aria-hidden and sit inside a link that carries its own
         aria-label, so their text is announced nowhere. The label is the only
         place the word can live for a screen reader, and it has to arrive and
         leave with the mark or the two drift apart. */
      if (baseAria) link.setAttribute("aria-label", baseAria + ", " + label);
    }

    function retire(persist) {
      Array.prototype.forEach.call(marks, function (m) { m.classList.remove("is-armed"); });
      if (baseAria) link.setAttribute("aria-label", baseAria);
      if (persist) { state.seen = true; write(state); }
    }

    function read() {
      try { return JSON.parse(localStorage.getItem(NEW_KEY)) || {}; }
      catch (e) { return {}; }
    }
    function write(next) {
      try { localStorage.setItem(NEW_KEY, JSON.stringify(next)); }
      catch (e) { /* private mode — the flag simply returns next visit */ }
    }

    /* Is the visitor already looking at the page the flag points to? Compared
       on pathname only: the href may be relative, absolute or carry a query,
       and a trailing slash is not a different page. */
    function onTarget() {
      var href = link.getAttribute("href") || "";
      if (!href) return false;
      try {
        var target = new URL(href, window.location.origin);
        if (target.host !== window.location.host) return false;
        return target.pathname.replace(/\/+$/, "") ===
               window.location.pathname.replace(/\/+$/, "");
      } catch (e) { return false; }
    }

    var state = read();

    /* In the theme editor the flag is always on and nothing is written. A
       merchant who ticks the setting and sees an unchanged header because
       this browser dismissed the badge last week has been told the feature is
       broken, and they are not wrong to file it. */
    if (window.Shopify && window.Shopify.designMode) { arm(); return; }

    /* A different word is a different announcement. */
    if (state.v !== label) state = { v: label, t: 0, seen: false };
    if (state.seen) return;

    /* Their clock starts at first sight, and only once. */
    if (!state.t) { state.t = Date.now(); write(state); }

    /* Not persisted: an expiry is the merchant's setting expiring, not the
       visitor dismissing anything, and lengthening the window later should
       bring the flag back rather than find it already written off. */
    if (days && Date.now() - state.t > days * 864e5) return;

    if (onTarget()) return retire(true);

    arm();
    link.addEventListener("click", function () { retire(true); });
  }

  /* ── Delivery location ──────────────────────────────────────────────────
     The app opens LocationModal against the customer's saved addresses; this
     is that, folded into a popover.

     WHAT DECIDES WHICH PANE OPENS

       logged out            → pin      (a pincode is all a guest can give us)
       signed in, no book    → pin + "Add New Address"
       signed in, has book   → list     (selectable, mirrors AddressSheet)

     The middle case is the one worth defending. A customer who has saved
     nothing is usually asking "do you even deliver here?", and answering that
     with a nine-field form is answering a different question. The form stays
     one click away rather than in the way.

     WHERE THE ADDRESSES COME FROM

     /apps/cnc/addresses — a Shopify App Proxy in front of cnc-api. The
     customer id is NOT sent from here; Shopify appends it from its own session
     cookie and signs the query string, and the API verifies that signature
     before it touches Firestore. That is why this fetch passes credentials and
     no identity: the cookie is the identity, and anything this page claimed
     about who it is would be ignored.

     The collection behind it is users/{key}/addresses — the same documents the
     app reads. Saving here shows up there, which is the entire point.

     WHAT IS STILL LOCAL

     The SELECTED address is a browser preference, not account state, so it
     lives in localStorage. Selecting is a display choice on one device; it must
     not reach across and change which address the app calls primary. */

  var ADDR_KEY = "cnc:address";
  var PIN_LOOKUP = "https://api.postalpincode.in/pincode/";

  /* Parse a JSON response WITHOUT trusting that there is one.
     `/apps/cnc/*` is a Shopify App Proxy, and when it isn't configured — or
     points at a deploy that lacks the route — Shopify answers with an empty
     body or a themed 404 page. Calling r.json() on that throws

         Failed to execute 'json' on 'Response': Unexpected end of JSON input

     which tells the customer nothing and tells us the wrong thing: it reads
     like a serialisation bug in code that is fine. Read the text, try to parse
     it, and if it isn't JSON say what that actually means. */
  function readJson(res) {
    return res.text().then(function (body) {
      var data = null;
      if (body) { try { data = JSON.parse(body); } catch (e) { data = null; } }
      if (data === null) {
        var why = res.status === 404 || !body
          ? "the /apps/cnc App Proxy isn't set up (or cnc-api has no /api/addresses yet)"
          : "the server replied with " + res.status + " and something that wasn't JSON";
        var err = new Error("Address service unavailable — " + why);
        err.proxyMissing = true;
        throw err;
      }
      return { ok: res.ok, data: data };
    });
  }

  /* Two sources describe the same address book and they overlap.

     Shopify's copy is server-rendered into the page (see the Liquid), the
     app's lives in Firestore behind the proxy, and an address saved from this
     header is written to BOTH — so it comes back twice. Deduped on
     shopifyAddressId when we have one, since that's exact; otherwise on
     pincode plus a squashed house line, which is what actually distinguishes
     two addresses a human would call different.

     Firestore wins ties: it carries addrType, the real name and the billing
     flag, where Shopify only has a mailing address. */
  function addrKey(a) {
    if (a.shopifyAddressId) return "s:" + a.shopifyAddressId;
    return "h:" + (a.pincode || "") + "|" +
      String(a.house || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  function mergeAddresses(shopify, firestore) {
    var out = [], seen = {};
    (firestore || []).concat(shopify || []).forEach(function (a) {
      var k = addrKey(a);
      if (seen[k]) return;
      seen[k] = 1;
      out.push(a);
    });
    /* Primary first, then leave the incoming order alone — both sources
       already sort newest-first and re-sorting here would fight them. */
    return out.sort(function (a, b) {
      return (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0);
    });
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    /* textContent, never innerHTML. Every string below is customer-authored
       and round-trips through Firestore; the moment one of them is parsed as
       markup, a saved address becomes stored XSS on every page of the store. */
    if (text != null) n.textContent = text;
    return n;
  }

  function addrLines(a) {
    var street = [a.house, a.locality].filter(Boolean).join(", ");
    var region = [a.city, a.state].filter(Boolean).join(", ");
    return [street, region + (a.pincode ? " - " + a.pincode : "")]
      .filter(function (s) { return s && s.trim(); });
  }

  function initLocation(root) {
    var wrap = root.querySelector("[data-cnc-hd-loc]");
    if (!wrap) return;
    var btn   = wrap.querySelector("[data-cnc-hd-loc-btn]");
    var pop   = wrap.querySelector("[data-cnc-hd-pop]");
    var input = wrap.querySelector("[data-cnc-hd-pin]");
    var save  = wrap.querySelector("[data-cnc-hd-pin-save]");
    var msg   = wrap.querySelector("[data-cnc-hd-pin-msg]");
    var value = wrap.querySelector("[data-cnc-hd-loc-value]");
    var listEl = wrap.querySelector("[data-cnc-hd-addrs]");
    var panes  = wrap.querySelectorAll("[data-cnc-pane]");
    /* Document-scoped, not wrap-scoped: the dialog is a sibling of the popover
       and gets moved to <body> on first open (see openModal). Anything looked
       up through `wrap` would be found once and then go stale. */
    var form    = document.querySelector("[data-cnc-hd-form-addr]");
    var formMsg = document.querySelector("[data-cnc-hd-form-msg]");
    if (!btn || !pop) return;

    var endpoint = wrap.dataset.addrEndpoint || "/apps/cnc/addresses";
    var signedIn = wrap.dataset.signedIn === "1";

    /* Seeded from Liquid, so the customer's Shopify address book is on screen
       the instant the popover opens — no request, no spinner, and no
       dependency on the proxy existing. `merged` stays false until Firestore
       has been folded in; it's what stops us fetching twice. */
    var shopAddrs = [];
    try { shopAddrs = JSON.parse(wrap.dataset.addresses || "[]") || []; } catch (e) { shopAddrs = []; }
    shopAddrs.forEach(function (a, i) { a.id = a.id || "shopify-" + i; });

    var addrs = signedIn ? shopAddrs.slice() : [];
    var merged = false;    // has the Firestore side been folded in yet?
    var fetching = null;   // in-flight promise, so a double-click is one request
    var selectedId = "";
    var addrType = "HOME";

    /* ── Header face ─────────────────────────────────────────────────────
       Once a pincode is known it REPLACES the word "Location" rather than
       sitting next to it. "Location 560001" reads as a label with a stray
       number after it; "560001" next to a map pin is self-explanatory, and it
       buys back the width the digits cost. `has-pin` drives that in CSS.

       Only ever the pincode, never the street — the requirement, and also the
       right call: a header is not the place to print someone's home address to
       whoever is looking over their shoulder. */
    function paintPin(pin) {
      if (value) value.textContent = pin || "";
      wrap.classList.toggle("has-pin", !!pin);
      btn.setAttribute("aria-label", pin ? "Delivering to " + pin + ", change" : "Set delivery location");
    }

    function readStore(key) {
      try { return localStorage.getItem(key) || ""; } catch (e) { return ""; }
    }
    function writeStore(key, val) {
      try { localStorage.setItem(key, val); } catch (e) { /* private mode */ }
    }

    var savedPin = readStore(PIN_KEY);
    try { selectedId = (JSON.parse(readStore(ADDR_KEY) || "{}") || {}).id || ""; } catch (e) { selectedId = ""; }
    if (savedPin && input) input.value = savedPin;
    paintPin(savedPin);

    /* The "Add New Address" button sits in the markup for everyone and is
       revealed by this class, because signing in must not require a page load
       to make it appear — and because a guest offered an address form they
       can't save to anything is a dead end dressed as a feature. */
    function paintAuth() { wrap.classList.toggle("can-add", signedIn); }
    paintAuth();

    /* ── Panes ───────────────────────────────────────────────────────────── */
    function showPane(name) {
      Array.prototype.forEach.call(panes, function (p) {
        p.hidden = p.dataset.cncPane !== name;
      });
    }

    /* Where "back" and "cancel" land, and where a fresh open lands. Derived
       every time rather than remembered, because the book changes underneath
       it — save the first address and the resting pane stops being `pin`. */
    function homePane() {
      return (signedIn && addrs && addrs.length) ? "list" : "pin";
    }

    function open(state) {
      pop.hidden = !state;
      btn.setAttribute("aria-expanded", String(state));
      if (!state) return;

      /* Paint FIRST, fetch second.

         The Shopify addresses are already here, so the popover opens fully
         populated on the same frame. The proxy call then runs in the
         background and repaints if it adds anything — an app-only address, or
         a richer copy of one we already show.

         The spinner survives for one case only: signed in with nothing
         server-rendered, where there genuinely is nothing to draw yet. Showing
         it when we already have addresses would be replacing real content with
         a loading state, which is strictly worse than the content. */
      var blank = signedIn && !merged && !addrs.length;
      if (blank) showPane("loading");
      else paintHome();

      if (signedIn && !merged) load().then(paintHome, paintHome);
    }

    function paintHome() {
      var pane = homePane();
      if (pane === "list") renderList();
      showPane(pane);
      var focusTarget = pane === "pin"
        ? input
        : (listEl && listEl.querySelector('.cnc-hd-addr[tabindex="0"]'));
      if (focusTarget) { try { focusTarget.focus(); } catch (e) { /* hidden */ } }
    }

    /* ── Load ────────────────────────────────────────────────────────────── */
    function load() {
      if (fetching) return fetching;
      fetching = fetch(endpoint, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
        .then(readJson)
        .then(function (res) {
          if (!res.ok || !res.data.ok) throw new Error(res.data.error || "HTTP error");
          /* On whether there's a session, the server is the authority — but
             only when it disagrees downwards AND we have nothing rendered.
             Liquid printed these addresses because Shopify itself said the
             customer was logged in; discarding them because a proxy that may
             not even be deployed said `signedIn: false` would replace real
             data with an outage. */
          if (res.data.signedIn === false && !shopAddrs.length) signedIn = false;
          addrs = mergeAddresses(shopAddrs, res.data.addresses || []);
          merged = true;
          paintAuth();
        })
        .catch(function (err) {
          /* Keep whatever Liquid gave us and carry on. The customer's actual
             goal — see my addresses, or at least tell me if you deliver here —
             is still served, and a red box in a header helps nobody.
             `merged` stays false so a later open retries. */
          console.error("[cnc-header] " + (err && err.message || err));
        })
        .then(function () { fetching = null; });
      return fetching;
    }

    /* ── List ────────────────────────────────────────────────────────────── */
    function renderList() {
      if (!listEl) return;
      listEl.textContent = "";
      /* Roving tabindex: the group is ONE tab stop and the arrow keys move
         within it. Declaring role="radio" and then leaving every card
         separately tabbable is the common half-implementation — a screen
         reader announces "1 of 4" while Tab walks all four, and the two
         accounts of the widget disagree. Falls back to the first card so the
         group is always reachable, even before anything is chosen. */
      var focusIndex = (addrs || []).findIndex(function (a) { return a.id === selectedId; });
      if (focusIndex < 0) focusIndex = 0;

      (addrs || []).forEach(function (a, i) {
        var on = a.id === selectedId;
        var card = el("button", "cnc-hd-addr" + (on ? " is-on" : ""));
        card.type = "button";
        card.setAttribute("role", "radio");
        card.setAttribute("aria-checked", String(on));
        card.tabIndex = i === focusIndex ? 0 : -1;
        card.dataset.id = a.id;

        card.appendChild(el("span", "cnc-hd-dot"));

        var body = el("span", "cnc-hd-addr-body");
        var head = el("span", "cnc-hd-addr-head");
        head.appendChild(el("strong", null, a.name || a.addrType || "Address"));
        if (a.addrType) head.appendChild(el("span", "cnc-hd-tag", a.addrType));
        if (a.isPrimary) head.appendChild(el("span", "cnc-hd-tag cnc-hd-tag--def", "DEFAULT"));
        body.appendChild(head);

        addrLines(a).forEach(function (line) {
          body.appendChild(el("span", "cnc-hd-addr-line", line));
        });
        if (a.mobile) body.appendChild(el("span", "cnc-hd-addr-mob", a.mobile));

        card.appendChild(body);
        listEl.appendChild(card);
      });
    }

    function selectAddress(a) {
      if (!a) return;
      selectedId = a.id;
      writeStore(ADDR_KEY, JSON.stringify({ id: a.id, pincode: a.pincode || "" }));
      commitPin(a.pincode || "", { silent: true });
      renderList();
      setTimeout(function () { open(false); }, 220);
    }

    if (listEl) {
      listEl.addEventListener("click", function (e) {
        var card = e.target.closest ? e.target.closest(".cnc-hd-addr") : null;
        if (!card || !listEl.contains(card)) return;
        var match = (addrs || []).filter(function (a) { return a.id === card.dataset.id; })[0];
        selectAddress(match);
      });

      /* Arrow keys move focus and Space/Enter chooses. Movement alone does NOT
         select: in this widget selecting dismisses the popover, so the usual
         "arrow keys also check the radio" behaviour would slam the dialog shut
         the moment someone pressed Down to read the next address. */
      listEl.addEventListener("keydown", function (e) {
        var keys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft", "Home", "End"];
        if (keys.indexOf(e.key) === -1) return;
        var cards = listEl.querySelectorAll(".cnc-hd-addr");
        if (!cards.length) return;
        var at = Array.prototype.indexOf.call(cards, document.activeElement);
        if (at === -1) at = 0;

        var next;
        if (e.key === "Home") next = 0;
        else if (e.key === "End") next = cards.length - 1;
        else {
          var step = (e.key === "ArrowDown" || e.key === "ArrowRight") ? 1 : -1;
          next = (at + step + cards.length) % cards.length;   // wraps, as radios do
        }

        e.preventDefault();
        cards[at].tabIndex = -1;
        cards[next].tabIndex = 0;
        cards[next].focus();
      });
    }

    /* ── Pincode ─────────────────────────────────────────────────────────── */
    function commitPin(v, opts) {
      var pin = String(v || "").replace(/[^\d]/g, "").slice(0, 6);
      if (pin.length !== 6) {
        if (!opts || !opts.silent) {
          msg.textContent = "Enter a valid 6-digit pincode";
          msg.className = "cnc-hd-pop-msg is-bad";
        }
        return false;
      }
      writeStore(PIN_KEY, pin);
      paintPin(pin);
      if (input) input.value = pin;
      if (!opts || !opts.silent) {
        msg.textContent = "Delivering to " + pin;
        msg.className = "cnc-hd-pop-msg is-good";
      }
      document.dispatchEvent(new CustomEvent("cnc:pincode", { detail: { pincode: pin } }));
      return true;
    }

    if (save) save.addEventListener("click", function () {
      if (commitPin(input.value)) setTimeout(function () { open(false); }, 700);
    });
    if (input) input.addEventListener("keydown", function (e) {
      if (e.key !== "Enter") return;
      e.preventDefault();
      if (commitPin(input.value)) setTimeout(function () { open(false); }, 700);
    });

    /* ── Navigation ──────────────────────────────────────────────────────── */
    /* ── Add-address modal ───────────────────────────────────────────────
       Scoped to the whole document, not to `root`: initModal moves the node to
       <body> the first time it opens, at which point it is no longer inside
       the header and root.querySelector would stop finding it. */
    var modal = document.querySelector("[data-cnc-hd-modal]");
    var lastFocus = null;
    var reparented = false;

    function openModal(state) {
      if (!modal) return;
      if (state) {
        /* Out of the header and onto <body>.

           The header is a stacking context — it sets z-index for its sticky
           behaviour — and its section wrapper becomes one too once sticky. A
           position:fixed child of either is confined to that context, so the
           dialog rendered UNDER the header it was opened from, backdrop and
           all. No z-index on the dialog can win that; only leaving the context
           can. */
        if (!reparented) { document.body.appendChild(modal); reparented = true; }
        lastFocus = document.activeElement;
        open(false);                 // one overlay at a time
        modal.hidden = false;
        document.documentElement.classList.add("cnc-hd-modal-open");
        var first = form && form.querySelector('input[name="name"]');
        if (first) { try { first.focus(); } catch (e) { /* hidden */ } }
      } else {
        modal.hidden = true;
        document.documentElement.classList.remove("cnc-hd-modal-open");
        if (lastFocus) { try { lastFocus.focus(); } catch (e) { /* gone */ } }
      }
    }

    Array.prototype.forEach.call(wrap.querySelectorAll("[data-cnc-hd-addnew]"), function (b) {
      b.addEventListener("click", function () {
        if (!form) return;   // dialog removed from the section — nothing to open
        clearFormErrors();
        openModal(true);
      });
    });

    /* Drag-to-close for the phone bottom sheet. Only the handle and the head
       start a drag, so scrolling the form never fights it. Past 110px (or a
       quick flick) it closes; otherwise it springs back. */
    if (modal) (function () {
      var box = modal.querySelector(".cnc-hd-modal-box");
      if (!box) return;
      var zones = modal.querySelectorAll("[data-cnc-hd-grab], [data-cnc-hd-grab-zone]");
      var y0 = 0, dy = 0, t0 = 0, dragging = false;
      function isSheet() { return window.matchMedia("(max-width: 599px)").matches; }
      function down(e) {
        if (!isSheet() || e.target.closest("button")) return;
        dragging = true; dy = 0; y0 = e.clientY; t0 = Date.now();
        box.classList.remove("is-settling");
        box.classList.add("is-dragging");
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* old browser */ }
      }
      function move(e) {
        if (!dragging) return;
        dy = Math.max(0, e.clientY - y0);
        box.style.transform = "translateY(" + dy + "px)";
      }
      function up() {
        if (!dragging) return;
        dragging = false;
        box.classList.remove("is-dragging");
        var fast = dy > 40 && (Date.now() - t0) < 250;
        box.classList.add("is-settling");
        if (dy > 110 || fast) {
          box.style.transform = "translateY(100%)";
          setTimeout(function () {
            box.classList.remove("is-settling");
            box.style.transform = "";
            openModal(false);
          }, 200);
        } else {
          box.style.transform = "";
          setTimeout(function () { box.classList.remove("is-settling"); }, 220);
        }
      }
      Array.prototype.forEach.call(zones, function (z) {
        z.addEventListener("pointerdown", down);
        z.addEventListener("pointermove", move);
        z.addEventListener("pointerup", up);
        z.addEventListener("pointercancel", up);
      });
    })();

    if (modal) {
      Array.prototype.forEach.call(
        modal.querySelectorAll("[data-cnc-hd-modal-close], [data-cnc-hd-modal-back]"),
        function (b) { b.addEventListener("click", function () { openModal(false); }); }
      );
      /* Escape closes the DIALOG, and the popover's own Escape handler must not
         also fire — hence stopPropagation. Without it one keypress dismissed
         both, and the customer lost the list they were coming back to. */
      modal.addEventListener("keydown", function (e) {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        openModal(false);
      });
    }

    var usePin = wrap.querySelector("[data-cnc-hd-usepin]");
    if (usePin) usePin.addEventListener("click", function () {
      showPane("pin");
      if (input) input.focus();
    });

    btn.addEventListener("click", function (e) {
      e.preventDefault();
      open(pop.hidden);
    });
    document.addEventListener("click", function (e) {
      if (!pop.hidden && !wrap.contains(e.target)) open(false);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !pop.hidden) { open(false); btn.focus(); }
    });

    /* ── Form ────────────────────────────────────────────────────────────── */
    if (!form) return;

    var typeBtns = form.querySelectorAll("[data-type]");
    Array.prototype.forEach.call(typeBtns, function (b) {
      b.addEventListener("click", function () {
        addrType = b.dataset.type;
        Array.prototype.forEach.call(typeBtns, function (o) {
          var on = o === b;
          o.classList.toggle("is-on", on);
          o.setAttribute("aria-checked", String(on));
        });
      });
    });

    function field(name) { return form.querySelector('[name="' + name + '"]'); }
    function errNode(name) { return form.querySelector('[data-err="' + name + '"]'); }

    function setErr(name, text) {
      var f = field(name), e = errNode(name);
      if (f) f.classList.toggle("is-bad", !!text);
      if (e) e.textContent = text || "";
    }
    function clearFormErrors() {
      ["name", "mobile", "house", "pincode", "city", "state"].forEach(function (n) { setErr(n, ""); });
      if (formMsg) { formMsg.textContent = ""; formMsg.className = "cnc-hd-pop-msg"; }
    }

    field("mobile").addEventListener("input", function (e) {
      e.target.value = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    });

    /* Pincode → city/state, same public lookup the app's AddAddressScreen
       uses. Autofilled, never locked: the API returns the post-office district,
       which is not always what the customer calls their city, and an
       uncorrectable field would make the address wrong in a way they can see
       and can't fix. A failed lookup is silent for the same reason — the
       fields are already there to type into. */
    var pinField = field("pincode");
    var pinState = form.querySelector("[data-cnc-hd-pinstate]");
    var pinSeq = 0;
    function setPinState(cls, text) {
      if (!pinState) return;
      pinState.className = "cnc-hd-pinstate" + (cls ? " " + cls : "");
      pinState.textContent = text || "";
    }
    /* A value WE filled may be replaced by the next lookup; a value the
       customer typed never is. Typing into the field drops the flag. */
    ["city", "state"].forEach(function (n) {
      field(n).addEventListener("input", function (e) { delete e.target.dataset.auto; });
    });
    function autofill(n, v) {
      var f = field(n);
      if (!v || (f.value && !f.dataset.auto)) return;
      f.value = v;
      f.dataset.auto = "1";
      setErr(n, "");
    }
    pinField.addEventListener("input", function (e) {
      var v = e.target.value.replace(/[^\d]/g, "").slice(0, 6);
      e.target.value = v;
      setErr("pincode", "");
      var seq = ++pinSeq;
      if (v.length !== 6) { setPinState("", ""); return; }
      setPinState("is-busy", "Checking pincode…");
      fetch(PIN_LOOKUP + v)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (seq !== pinSeq) return;          // a newer pincode is in flight
          var po = d && d[0] && d[0].Status === "Success" && d[0].PostOffice && d[0].PostOffice[0];
          if (!po) { setPinState("", ""); return; }
          var city = po.District || po.Block || "";
          autofill("city", city);
          autofill("state", po.State || "");
          setPinState("is-ok", "Delivering to " + [city, po.State].filter(Boolean).join(", "));
        })
        .catch(function () { if (seq === pinSeq) setPinState("", ""); /* type it manually */ });
    });

    function collect() {
      return {
        name:     field("name").value.trim(),
        mobile:   field("mobile").value.replace(/[^\d]/g, ""),
        house:    field("house").value.trim(),
        locality: field("locality").value.trim(),
        pincode:  field("pincode").value.replace(/[^\d]/g, ""),
        city:     field("city").value.trim(),
        state:    field("state").value.trim(),
        addrType: addrType,
        isPrimary: field("isPrimary").checked,
        isBilling: field("isBilling").checked
      };
    }

    /* Mirrors AddAddressScreen's `canSave` exactly. The server re-checks all of
       it — this copy exists to answer instantly, not to be the gate. */
    function validate(a) {
      var errs = {};
      if (!a.name) errs.name = "Name is required";
      if (a.mobile.length !== 10) errs.mobile = "Enter a valid 10-digit mobile number";
      if (!a.house) errs.house = "House / flat number is required";
      if (a.pincode.length !== 6) errs.pincode = "Enter a valid 6-digit pincode";
      if (!a.city) errs.city = "City is required";
      if (!a.state) errs.state = "State is required";
      return errs;
    }

    var saving = false;
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (saving) return;

      var address = collect();
      var errs = validate(address);
      clearFormErrors();
      var bad = Object.keys(errs);
      if (bad.length) {
        bad.forEach(function (n) { setErr(n, errs[n]); });
        var firstBad = field(bad[0]);
        if (firstBad) firstBad.focus();
        return;
      }

      saving = true;
      if (modal) modal.classList.add("is-saving");
      if (formMsg) { formMsg.textContent = "Saving…"; formMsg.className = "cnc-hd-pop-msg"; }

      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ address: address })
      })
        .then(readJson)
        .then(function (res) {
          if (!res.ok || !res.data.ok) {
            /* Field-level errors from the server are painted onto the same
               nodes the client validator uses, so a rule only this side knows
               about still lands next to the field it's about. */
            var srv = res.data.errors || null;
            if (srv) Object.keys(srv).forEach(function (n) { setErr(n, srv[n]); });
            throw new Error(res.data.error || "Could not save address");
          }

          /* The server writes to Shopify AND Firestore and hands back the
             combined book, so this replaces rather than merges. shopAddrs is
             refreshed too, or the next background load would re-merge the
             stale server-rendered copy and resurrect the pre-save list. */
          addrs = mergeAddresses([], res.data.addresses || []);
          shopAddrs = addrs.slice();
          merged = true;
          signedIn = true;

          form.reset();
          setPinState("", "");
          ["city", "state"].forEach(function (n) { delete field(n).dataset.auto; });
          addrType = "HOME";
          Array.prototype.forEach.call(typeBtns, function (o) {
            var on = o.dataset.type === "HOME";
            o.classList.toggle("is-on", on);
            o.setAttribute("aria-checked", String(on));
          });
          if (formMsg) { formMsg.textContent = ""; formMsg.className = "cnc-hd-pop-msg"; }

          openModal(false);
          paintAuth();
          /* Saving one implies choosing it — nobody adds an address in order
             to then pick a different one. The header's pincode changing is the
             confirmation; reopening the popover just to close it again 200ms
             later would be a flicker, not feedback. */
          selectAddress(res.data.address || addrs[0]);
        })
        .catch(function (err) {
          if (formMsg) {
            formMsg.textContent = err.message || "Could not save address";
            formMsg.className = "cnc-hd-pop-msg is-bad";
          }
        })
        .then(function () {
          saving = false;
          if (modal) modal.classList.remove("is-saving");
        });
    });
  }

  /* ── CNC Club ─────────────────────────────────────────────
     Nothing to initialise. The rewards control is an ordinary link to the
     benefits page.

     There was an initPoints() here that fetched a live balance. It's gone, and
     the reason it went has since half-expired, so read this before putting it
     back:

       · The original objection was that points could only be SPENT in the app,
         making a balance in the header a number the customer could read and
         not use. THAT IS NO LONGER TRUE — points are redeemed on this site now
         (see pointsCardHtml in cnc-app.js), so a header balance would be
         actionable.
       · The second objection stands. Sourcing it safely means a request per
         page load through the signed App Proxy, on every page, for a figure
         nobody came to the header to read. The balance belongs where it is
         spent — the bag — and where it is reviewed: /pages/rewards.

     So: still no balance here, but on cost now rather than on principle. If it
     ever earns its request, /apps/cnc/addresses?resource=rewards is the read
     and SHARED.points in cnc-app.js already caches it for the page. */

  /* ── Sticky + scroll shadow ─────────────────────────────────────────────
     Two jobs:

     1. Promote the Shopify section wrapper to the sticky element. See the
        long note in cnc-header.css — sticky on the <header> itself pins it
        inside a box it already fills, which is indistinguishable from sticky
        not working. The CSS :has() rule covers modern browsers before JS
        runs; this covers the rest, and the case where the header was placed
        by hand in theme.liquid with no .shopify-section around it.

     2. Add `is-stuck` once the header has actually left its resting place, so
        the shadow only appears when there's content underneath to cast it on.

     The threshold is measured, not assumed to be zero: an announcement bar
     above the header means it starts partway down the page and doesn't stick
     until the scroll passes that offset. */
  function initSticky(root) {
    if (!root.classList.contains("is-sticky")) return;

    /* Only promote a parent that is genuinely the section wrapper. Blindly
       marking root.parentElement would make whatever container the theme
       happens to use — potentially one wrapping the entire page — sticky, and
       a page-height sticky element is a strange thing to inflict on a layout
       nobody asked us to touch. Anything else, the header sticks as itself. */
    var parent = root.parentElement;
    var isWrapper = !!parent && parent !== document.body &&
      (parent.classList.contains("shopify-section") ||
       parent.id.indexOf("shopify-section") === 0);

    var anchor;
    if (isWrapper) { parent.classList.add("cnc-hd-host"); anchor = parent; }
    else { root.classList.add("cnc-hd-self"); anchor = root; }

    var stickAt = 0;
    function measure() {
      /* Read the resting offset with the class off, or a header that's
         already stuck measures its own pinned position and the threshold
         creeps down the page on every resize. */
      var wasStuck = root.classList.contains("is-stuck");
      root.classList.remove("is-stuck");
      stickAt = anchor.getBoundingClientRect().top + window.pageYOffset;
      if (wasStuck) root.classList.add("is-stuck");
    }

    var ticking = false;
    function paint() {
      ticking = false;
      root.classList.toggle("is-stuck", window.pageYOffset > stickAt + 1);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    }

    measure();
    paint();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", function () { measure(); paint(); });
    /* Images and webfonts above the header change its resting offset after
       first paint; re-measure once everything has settled. */
    window.addEventListener("load", function () { measure(); paint(); });
  }

  /* ── Boot ───────────────────────────────────────────────────────────────
     Every init is wrapped. They are independent features sharing one loop, so
     an exception in any one of them used to abort the loop and take the rest
     with it — which is exactly what happened when initSticky went missing and
     the ReferenceError silently killed search, badges and the location
     popover on the same line. A header where one thing is broken is a bug; a
     header where everything is broken because one thing is, is an outage. */
  function boot() {
    var roots = document.querySelectorAll("[data-cnc-header]");
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.cncHeaderReady === "1") return;
      root.dataset.cncHeaderReady = "1";

      var parts = {
        sticky: initSticky, search: initSearch, hints: initHints,
        cart: initCart, badges: initBadges, location: initLocation,
        newbadge: initNewBadge
      };
      for (var name in parts) {
        if (!Object.prototype.hasOwnProperty.call(parts, name)) continue;
        try {
          parts[name](root);
        } catch (err) {
          /* Loud in the console, invisible to the customer, and — the point —
             the next feature still gets its turn. */
          console.error("[cnc-header] " + name + " failed to initialise", err);
        }
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // Theme editor re-renders the section on every setting change.
  document.addEventListener("shopify:section:load", boot);
})();
